import {
    type AnyKnittingDocument,
    type CrochetDocument,
    fromCrochetDocument,
    migrate,
    type PatternDocument,
    type SparseCell,
    toCrochetDocument,
    toKnittingDocumentV2,
} from '@kinnet-studio/pattern';

import type { CrochetSnapshot } from '@/crochet-editor/crochet-history';
import { convertLegacyCrochetDocument } from '@/crochet-editor/legacy-arrays/document';
import { isEmptySizingDefaults, sanitizeCrochetSizingDefaults, type CrochetSizingDefaults } from '@/crochet-editor/sizing-defaults';
import { isEmptyStrokeStyle, sanitizeStrokeStyle, type StrokeStyle } from '@/crochet-editor/stroke-style';

/** The pattern documents the app holds: crochet v2 (SP-130: a crochet v1
 *  document is converted to v2 when it is read, see `parseStoredDiagram`) and
 *  either knitting version. */
export type HeldPatternDocument = CrochetDocument | AnyKnittingDocument;

/** App-level persistence envelope. The `pattern` payload is package-owned:
 *  its shape, validation, and version migration belong to
 *  @kinnet-studio/pattern (`migrate`). The envelope carries app metadata plus
 *  editor state the package format doesn't hold yet (turningChains —
 *  follow-up: move into CrochetContent via a package PR). */
export type StoredDiagramV1 = {
    formatVersion: 1;
    id: string;
    name: string;
    createdAt: number;
    updatedAt: number;
    pattern: HeldPatternDocument;
    editor: { turningChains: string[]; sizingDefaults?: CrochetSizingDefaults; strokeStyle?: StrokeStyle };
};

export type StoredDiagram = StoredDiagramV1;

export class StoredDiagramError extends Error {}

/** The persisted `pattern` + `editor` slice of a StoredDiagram — everything
 *  produced from engine/document state, independent of envelope metadata
 *  (id/name/timestamps). */
export type StoredDiagramState = Pick<StoredDiagram, 'pattern' | 'editor'>;

export function newCrochetDiagram(
    name: string, sizingDefaults?: CrochetSizingDefaults, strokeStyle?: StrokeStyle,
): StoredDiagram {
    const now = Date.now();
    return {
        formatVersion: 1,
        id: crypto.randomUUID(),
        name,
        createdAt: now,
        updatedAt: now,
        pattern: {
            version: 2,
            kind: 'crochet',
            crochet: {
                stitches: [],
                guides: [],
                verticalLinks: [],
                stitchGroups: [],
                spaces: [],
                annotations: [],
            },
        },
        editor: {
            turningChains: [],
            ...(sizingDefaults != null && !isEmptySizingDefaults(sizingDefaults) ? { sizingDefaults } : {}),
            ...(strokeStyle != null && !isEmptyStrokeStyle(strokeStyle) ? { strokeStyle } : {}),
        },
    };
}

const NEW_KNITTING_ROWS = 10;
const NEW_KNITTING_COLS = 10;

/** New knitting documents seed the same 10×10 blank-chart footprint the grid
 *  editor starts with today (sparse 'empty' cells are real chart slots — an
 *  entirely empty document would open as a void canvas). */
export function newKnittingDiagram(name: string): StoredDiagram {
    const now = Date.now();
    const cells: SparseCell[] = [];
    for (let row = 0; row < NEW_KNITTING_ROWS; row++) {
        for (let col = 0; col < NEW_KNITTING_COLS; col++) {
            cells.push({ row, col, type: 'empty' });
        }
    }
    return {
        formatVersion: 1,
        id: crypto.randomUUID(),
        name,
        createdAt: now,
        updatedAt: now,
        pattern: toKnittingDocumentV2(cells),
        editor: { turningChains: [] },
    };
}

export function crochetEditorState(snap: CrochetSnapshot): StoredDiagramState {
    return {
        pattern: toCrochetDocument(snap),
        editor: {
            turningChains: [...snap.turningChains],
            ...(snap.sizingDefaults != null && !isEmptySizingDefaults(snap.sizingDefaults)
                ? { sizingDefaults: snap.sizingDefaults } : {}),
            ...(snap.strokeStyle != null && !isEmptyStrokeStyle(snap.strokeStyle) ? { strokeStyle: snap.strokeStyle } : {}),
        },
    };
}

/** `doc`'s crochet document as v2, with its turning chains. A `StoredDiagram`
 *  holds v2, as `parseStoredDiagram` converts v1 on read; one built from JSON
 *  that skipped it may still hold v1, stitch arrays and all, which is
 *  converted here too rather than read as v2, which would drop the arrays and
 *  let go of everything attached to them. Throws for a non-crochet document. */
function heldCrochetDocument(doc: StoredDiagram): { pattern: CrochetDocument; turningChains: readonly string[] } {
    const pattern = doc.pattern as PatternDocument;
    if (pattern.kind !== 'crochet') {
        throw new StoredDiagramError(`not a crochet document: ${pattern.kind}`);
    }
    if (pattern.version === 1) return convertLegacyCrochetDocument(pattern, doc.editor.turningChains);
    return { pattern, turningChains: doc.editor.turningChains };
}

/** What the engine loads, or a shared document is seeded with, for a stored
 *  crochet diagram: its content as the app holds it, with the editor values.
 *  Throws for a non-crochet document. */
export function storedToEngineSnapshot(doc: StoredDiagram): CrochetSnapshot {
    const { pattern, turningChains } = heldCrochetDocument(doc);
    const content = fromCrochetDocument(pattern);
    return {
        stitches: content.stitches,
        guides: content.guides,
        verticalLinks: content.verticalLinks,
        stitchGroups: content.stitchGroups ?? new Map(),
        spaces: content.spaces ?? new Map(),
        annotations: content.annotations ?? new Map(),
        turningChains: new Set(turningChains),
        sizingDefaults: doc.editor.sizingDefaults,
        strokeStyle: doc.editor.strokeStyle,
    };
}

/** The single load funnel. Envelope checked by hand (it is tiny); the pattern
 *  payload is validated + version-migrated by the package's zod-backed
 *  migrate(). Anything suspect throws — callers must treat a throw as
 *  "do not touch the stored document". */
export function parseStoredDiagram(raw: unknown): StoredDiagram {
    if (typeof raw !== 'object' || raw == null) {
        throw new StoredDiagramError('stored diagram is not an object');
    }
    const r = raw as Record<string, unknown>;
    if (r.formatVersion !== 1) {
        throw new StoredDiagramError(`unsupported formatVersion: ${String(r.formatVersion)}`);
    }
    if (
        typeof r.id !== 'string'
        || typeof r.name !== 'string'
        || typeof r.createdAt !== 'number'
        || typeof r.updatedAt !== 'number'
    ) {
        throw new StoredDiagramError('malformed envelope metadata');
    }
    const migrated = migrate(r.pattern);
    if (migrated.kind !== 'crochet' && migrated.kind !== 'knitting') {
        throw new StoredDiagramError(`unsupported document kind: ${(migrated as { kind: string }).kind}`);
    }
    const editor = typeof r.editor === 'object' && r.editor != null
        ? (r.editor as Record<string, unknown>)
        : {};
    let turningChains = Array.isArray(editor.turningChains)
        ? editor.turningChains.filter((x): x is string => typeof x === 'string')
        : [];
    // SP-125, SP-130: stitch arrays are gone from the app. A crochet v1
    // document (saved by an older build) is converted to v2 here, its arrays
    // turned into stitches, the one place every stored-JSON reader goes
    // through, so nothing downstream sees v1 or an array.
    let pattern: HeldPatternDocument;
    if (migrated.kind === 'crochet' && migrated.version === 1) {
        const converted = convertLegacyCrochetDocument(migrated, turningChains);
        pattern = converted.pattern;
        turningChains = converted.turningChains;
    } else {
        pattern = migrated;
    }
    const sizingDefaults = sanitizeCrochetSizingDefaults(editor.sizingDefaults);
    const strokeStyle = sanitizeStrokeStyle(editor.strokeStyle);
    return {
        formatVersion: 1,
        id: r.id,
        name: r.name,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
        pattern,
        editor: {
            turningChains,
            ...(!isEmptySizingDefaults(sizingDefaults) ? { sizingDefaults } : {}),
            ...(!isEmptyStrokeStyle(strokeStyle) ? { strokeStyle } : {}),
        },
    };
}
